import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  HostListener,
  inject,
  OnInit,
  ViewRef,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { formatDateByTimeZone, IControlOption } from '@app/shared';
import { ChangableComponent } from '@models/changable.component';
import { Store } from '@ngrx/store';
import { getMe } from '@store/reducers/profile.reducer';
import { User } from 'lingo2-models';
import { DateFnsModule } from 'lingo2-ngx-date-fns';
import { ClickOutsideModule } from 'ng-click-outside';
import { DeviceDetectorService } from 'ngx-device-detector';
import { OnUiSelectSearchModule } from 'onclass-ui';
import { combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AccountService, ConfigService, ContextService, ITimezone } from '../../services';

@Component({
  selector: 'app-timezone-selector',
  standalone: true,
  imports: [LocaleSharedModule, CommonModule, ClickOutsideModule, OnUiSelectSearchModule, FormsModule, DateFnsModule],
  templateUrl: './timezone-selector.component.html',
  styleUrls: ['./timezone-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimezoneSelectorComponent extends ChangableComponent implements OnInit {
  protected cdr = inject(ChangeDetectorRef);
  private accountService = inject(AccountService);
  private contextService = inject(ContextService);
  private configService = inject(ConfigService);
  private store = inject(Store);
  deviceService = inject(DeviceDetectorService);

  @HostBinding('class.responsive') isResponsive = window.innerWidth <= 1650;

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    this.isResponsive = event.target.innerWidth <= 1650;
  }
  public timezone: string = '';
  public timezones: IControlOption[];
  public listOpened = false;
  public me: User;
  searchQuery: string = '';

  // Show current time with timezone. For debug purposes
  public showTime: boolean = false;
  private timeTimer: any;
  public currentTime: Date;

  public ngOnInit() {
    this.configService.load(['timezones$']);
    combineLatest([this.store.select(getMe), this.configService.timezones$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: ([me, timezones]) => {
          if (timezones && me) {
            const tz = timezones.find((el) => el.name == me.timezone);
            this.me = me;
            if (tz) {
              this.timezone = this.deviceService.isMobile()
                ? `${this.timezoneOffsetToHours(tz.offset)}`
                : `(${this.timezoneOffsetToHours(tz.offset, false)}) ${tz.name}`;
              this.timezones = this.timezonesOptionsInit(timezones);
            } else {
              this.timezones = null;
            }
          }
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });

    if (this.showTime) {
      this.updateTime();
      // Обновляем время каждую секунду
      this.timeTimer = setInterval(() => {
        this.updateTime();
      }, 1000);
    }
  }

  ngOnDestroy(): void {
    if (this.timeTimer) {
      clearInterval(this.timeTimer);
    }
  }

  public timezonesOptionsInit(timezones: ITimezone[]): IControlOption[] {
    return timezones.map((tz) => ({
      title: `(${this.timezoneOffsetToHours(tz.offset)}) ${tz.name} - ${tz.cities.join(', ')}`,
      value: tz.name,
    }));
  }

  public filteredTimezones() {
    return this.timezones?.filter((option) => option.title.toLowerCase().includes(this.searchQuery.toLowerCase()));
  }

  public timezoneOffsetToHours(offset: number, withUTC: boolean = true): string {
    const sign = offset < 0 ? '-' : '+';
    const hhmm = new Date(Math.abs(offset) * 60 * 1000)?.toISOString().substr(11, 5);
    return (withUTC ? 'UTC: ' : '') + sign + hhmm;
  }

  public setTimezone(timezone: string) {
    this.closeList();
    this.updateAccountTimezone(timezone);
  }

  public updateAccountTimezone(timezone: string) {
    if (timezone === this.me.timezone) {
      return;
    }

    this.accountService
      .updateAccount({ timezone })
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (account) => {
          this.contextService.updateMe(account);
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public toggleList() {
    this.listOpened = !this.listOpened;
    this.searchQuery = '';
    this.detectChanges();
  }

  public closeList() {
    this.listOpened = false;
    this.detectChanges();
  }

  public trackByFn(index) {
    return index;
  }

  private updateTime(): void {
    const now = new Date();
    this.currentTime = formatDateByTimeZone(now, this.me?.timezone);
    this.detectDataChanges();
  }

  private detectDataChanges() {
    if (this.cdr && !(this.cdr as ViewRef).destroyed) {
      this.cdr.markForCheck();
    }
  }
}
